/**
 * useRouteLogCache — the climber's own route entries, read once in the
 * background, so the route modal's buttons are live on open
 * (docs/spec/11-map-routes.md, "Loading the state"; docs/spec/01f-tab-loading.md,
 * "The always-run prefetch").
 *
 * State lives in a ref: nothing renders from it. The modal reads it once, on
 * open (`peek`); every other change only has to be there by the next open.
 *
 * Two rules keep it honest:
 *
 * - A write to an OLDER day of a route calls `forget(rid)` before it writes and
 *   again once it is done, and the route stays forgotten until a per-route
 *   query stores it again — a prefetch already in flight cannot bring it back,
 *   and a per-route answer whose ticket predates the last `forget` is refused.
 * - The cache vouches only up to the day it last read or followed live
 *   (`liveDay`). Today's entries come from the live document (folded in on
 *   every change), but a day that ended while the listener was not attached —
 *   the app asleep in the background over midnight, while another device
 *   logged — was never seen. So when the gym's day moves past `liveDay`, the
 *   ended days are read again (one forced query) and nothing is served until
 *   that answer is in.
 *
 * It holds nothing until the gym membership is SERVER-confirmed
 * (`memberActive`, GymContext's `memberState`). Before that `fetchClimbLogs`
 * answers empty, and an empty window read stored as the truth would show ⚡ on
 * a project route for the rest of the visit. A prefetch asked for before the
 * confirmation runs once it arrives.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  routeLogWindowStart, groupClimbLogsByRoute, routeLogCacheCovers, foldDayIntoRouteLog, shiftDayKey, isDayKey,
} from "../utils/routeLogCache";

function emptyCache(key) {
  return {
    key,
    epoch: 0,
    wanted: false,           // the background prefetch asked for the read
    inFlight: false,         // the window read or a day re-read is out
    from: null,              // the window the prefetch read, or null before it
    liveDay: null,           // the newest day whose older days are known complete
    byRoute: new Map(),      // routeId → entries, newest first
    known: new Set(),        // routes stored by a per-route query (whole history)
    dropped: new Set(),      // routes a write forgot; not served until stored again
    versions: new Map(),     // routeId → forget counter, for the per-route tickets
  };
}

/**
 * @param {object} opts
 * @param {string|null} opts.uid
 * @param {string|null} opts.gymId
 * @param {boolean} opts.memberActive      the membership is server-confirmed
 * @param {string|null} opts.todayKey      the gym's day ("YYYY-MM-DD")
 * @param {Array}  opts.routes             the active routes (the window start)
 * @param {object|null} opts.todayClimbLog the live day document
 * @param {Function} opts.fetchClimbLogs   `(from, to, force) => Promise<dayDocs>`
 */
export function useRouteLogCache({ uid, gymId, memberActive, todayKey, routes, todayClimbLog, fetchClimbLogs }) {
  const visit = uid && gymId ? `${uid}|${gymId}` : null;
  const key = memberActive ? visit : null;
  const keyRef = useRef(key);
  keyRef.current = key;
  const visitRef = useRef(visit);
  visitRef.current = visit;
  // The visit the background prefetch asked for. It outlives a key that is
  // still null (membership not confirmed yet), so the read runs once it is.
  const wantedVisitRef = useRef(null);
  const latest = useRef({});
  latest.current = { uid, todayKey, routes, todayClimbLog, fetchClimbLogs };

  const cacheRef = useRef(emptyCache(key));
  const prefetchRef = useRef(null);
  // A gym switch or a sign-out empties it — never one climber's or gym's
  // entries under another key.
  const cache = useCallback(() => {
    if (cacheRef.current.key !== keyRef.current) cacheRef.current = emptyCache(keyRef.current);
    return cacheRef.current;
  }, []);

  const foldLive = useCallback((c) => {
    const { todayClimbLog: live, uid: me } = latest.current;
    if (live && live.date && live.userId === me) c.byRoute = foldDayIntoRouteLog(c.byRoute, live);
  }, []);

  /** Throw everything away; per-route queries refill it, and a still-wanted read runs again. */
  const reset = useCallback((c, { wanted }) => {
    cacheRef.current = { ...emptyCache(c.key), epoch: c.epoch + 1, wanted };
  }, []);

  // The gym's day moved past what the cache vouches for: read the ended days
  // again before serving anything.
  const resyncDays = useCallback(async (c) => {
    const { todayKey: today, fetchClimbLogs: fetchLogs } = latest.current;
    if (!c.key || !fetchLogs || c.inFlight || !isDayKey(c.liveDay) || !isDayKey(today) || today <= c.liveDay) return;
    const start = c.liveDay;
    const end = shiftDayKey(today, -1);
    const epoch = c.epoch;
    c.inFlight = true;
    let docs;
    try {
      docs = await fetchLogs(start, end, true);
    } catch {
      c.inFlight = false;
      if (cacheRef.current === c && c.epoch === epoch) reset(c, { wanted: false });
      return;
    }
    c.inFlight = false;
    if (cacheRef.current !== c || c.epoch !== epoch) return;
    const byDate = new Map((docs || []).map(d => [d.date, d]));
    // Every ended day, also one without a document (its entries, if the cache
    // held any, are gone).
    for (let d = start; d <= end; d = shiftDayKey(d, 1)) {
      c.byRoute = foldDayIntoRouteLog(c.byRoute, byDate.get(d) || { date: d, climbs: {} });
    }
    foldLive(c);
    c.liveDay = today;
    // The day may have moved again while the read was out.
    if (latest.current.todayKey > today) await resyncDays(c);
    // A window read asked for while this one was out.
    else if (c.wanted && !c.from) prefetchRef.current?.().catch(() => {});
  }, [foldLive, reset]);

  const prefetch = useCallback(async () => {
    const c = cache();
    const { todayKey: today, routes: list, fetchClimbLogs: fetchLogs } = latest.current;
    wantedVisitRef.current = visitRef.current;
    if (!c.key || !fetchLogs) return;
    // The prefetch's ready condition does not wait for the routes listener: with
    // no window yet, the read runs as soon as the routes arrive (effect below).
    c.wanted = true;
    if (c.inFlight) return;
    const from = routeLogWindowStart(list, today);
    if (!from) return;
    const epoch = c.epoch;
    c.inFlight = true;
    let docs;
    try {
      // Forced: the range cache of `fetchClimbLogs` may hold an older answer.
      docs = await fetchLogs(from, today, true);
    } catch (e) {
      // No retry on every routes change (a refused read would repeat forever):
      // the per-route query covers this visit.
      c.wanted = false;
      wantedVisitRef.current = null;
      throw e;
    } finally {
      c.inFlight = false;
    }
    if (cacheRef.current !== c || c.epoch !== epoch) return;
    const next = groupClimbLogsByRoute(docs);
    // A per-route answer also holds the days before `from`; the read is the
    // newer word on everything from `from` on.
    for (const rid of c.known) {
      const older = (c.byRoute.get(rid) || []).filter(e => e.date < from);
      if (older.length) next.set(rid, [...(next.get(rid) || []), ...older]);
    }
    c.byRoute = next;
    c.from = from;
    // Every day up to `today` was just read, for every route.
    if (!c.liveDay || today > c.liveDay) c.liveDay = today;
    // The live document may have moved while the read was out.
    foldLive(c);
    await resyncDays(c);
  }, [cache, foldLive, resyncDays]);
  prefetchRef.current = prefetch;

  /** The route's entries, or `null` when the cache cannot vouch for them. */
  const peek = useCallback((route) => {
    const c = cache();
    const rid = route?.id;
    if (!c.key || !rid || c.dropped.has(rid)) return null;
    if (c.liveDay && latest.current.todayKey > c.liveDay) return null;
    if (c.known.has(rid) || routeLogCacheCovers(c.from, route)) return c.byRoute.get(rid) || [];
    return null;
  }, [cache]);

  /** Taken before a per-route query; `store` refuses the answer if a write came in since. */
  const ticket = useCallback((rid) => {
    const c = cache();
    return `${c.key}|${c.epoch}|${c.versions.get(rid) || 0}`;
  }, [cache]);

  const store = useCallback((rid, entries, tk) => {
    const c = cache();
    if (!c.key || !rid || tk !== `${c.key}|${c.epoch}|${c.versions.get(rid) || 0}`) return;
    const next = new Map(c.byRoute);
    next.set(rid, Array.isArray(entries) ? entries : []);
    c.byRoute = next;
    c.known.add(rid);
    c.dropped.delete(rid);
    // The answer is complete up to today; a cache that vouched for nothing yet
    // follows from here.
    if (!c.liveDay) c.liveDay = latest.current.todayKey;
    foldLive(c);
  }, [cache, foldLive]);

  const forget = useCallback((rid) => {
    const c = cache();
    if (!rid) return;
    c.versions.set(rid, (c.versions.get(rid) || 0) + 1);
    c.dropped.add(rid);
    c.known.delete(rid);
    if (c.byRoute.has(rid)) {
      const next = new Map(c.byRoute);
      next.delete(rid);
      c.byRoute = next;
    }
  }, [cache]);

  // Still wanted, so the read runs again on the next routes change; the
  // per-route queries refill it meanwhile.
  const forgetAll = useCallback(() => {
    const c = cache();
    reset(c, { wanted: c.wanted });
  }, [cache, reset]);

  // Also on the membership confirmation (`key`): a prefetch asked for this
  // visit before it found no key to read under.
  useEffect(() => {
    const c = cache();
    const wanted = c.wanted || (!!c.key && wantedVisitRef.current === c.key);
    if (wanted && !c.from && !c.inFlight) prefetch().catch(() => {});
  }, [routes, todayKey, key, cache, prefetch]);

  // Every change of the live day document goes in; a new gym day re-reads the
  // ended one(s) first.
  useEffect(() => {
    const c = cache();
    if (!c.key) return;
    foldLive(c);
    resyncDays(c);
  }, [todayClimbLog, todayKey, uid, cache, foldLive, resyncDays]);

  return useMemo(
    () => ({ prefetch, peek, ticket, store, forget, forgetAll }),
    [prefetch, peek, ticket, store, forget, forgetAll],
  );
}
